import * as React from "react";
import {Subscription} from "rxjs";
import {filter} from "rxjs/operators";
import {
    ComponentCategory,
    ComponentElementManifest,
    ComponentElementProperty
} from "@bizform/type/model/component_element/ComponentElementManifest.model";
import {Static} from "@bizform/type/decorator/Static.decorator";
import {
    BaseStateModel,
    ComponentElement,
    ComponentElementStatic
} from "@bizform/type/model/component_element/ComponentElement.model";
import {DefaultLayoutId} from "@bizform/type/model/component_element/DefaultLayoutId.model";
import {PluginType} from "@bizform/type/model/model/Plugin.model";
import {DomAttribute} from "@bizform/type/model/dom/DomAttribute.model";
import {
    GridCloseActionKeyConstant,
    GridIdKeyConstant,
    GridLinkIdKeyConstant
} from "@bizform/type/model/component_element/manifest_constant/Grid.constant";
import {GridSubject, GridSubjectType} from "@bizform/type/model/model/Subject.model";
import {ComponentHelper, Debug, StringHelper} from "@bizform/helper/dist/js/bizflow/helper.min";
import {
    ComponentElementClass,
    FormValidationService,
    ReactComponentElementInit,
    SubjectService
} from "@bizform/core/dist/js/bizflow/core.min";
import {AbstractComponentElement} from "../bf_component/AbstractComponentElement";
import {GridHelper} from "./helper/Grid.helper";


type Props =  {
};

type State = BaseStateModel & {
    [GridIdKeyConstant]?: string;
    pageLinkSize?: number;
    paginator?: boolean;
    rows?: number;
    containerClassName?: string;
    containerStyle?: object;
    headerClassName?: string;
    headerStyle?: object;
    bodyClassName?: string;
    bodyStyle?: object;
    naviClassName?: string;
    naviStyle?: object;
    footerClassName?: string;
    footerStyle?: object;
};

/**
 * Grid Action Container
 *
 * @since 0.0.1
 * @author Heungsoo Ham
 * @class
 */
@ComponentElementClass()
@Static<ComponentElementStatic<string>>()
export class BfGridMDetailContainer extends AbstractComponentElement<Props, State> {
    static readonly pluginType: string = PluginType.GRIDHANDLER;

    /* manifest */
    static readonly componentElementManifest: ComponentElementManifest = {
        uniqueId: "_30_WaWcarRcbTZmSlJuZ4s14uuTOd1a",
        category: ComponentCategory.HANDLER,
        defaultLayoutId: DefaultLayoutId.ONLYCOMPONENTELEMENT,
        label: "Detail Container",
        description: "Detail Container",
        icon: "",
        property: null
    };

    setProperty(property: ComponentElementProperty) {
        try {
            const newState = ComponentHelper.createStateWithProperties<State, null>(
                property,
                this);
            this.setState(newState);
        } catch (e) {
            Debug.console.error("setProperty", e);
        }
    }

    noDisplayInheritance() {
    }

    @ReactComponentElementInit(BfGridMDetailContainer)
    static init(): ComponentElement<string> {
        return;
    }

    initDone(): void {
        if (!this._gridSubject) {
            this._gridSubject = SubjectService.gridSubject.pipe(
                filter((data: GridSubject) =>
                    (this.state && data.formId === this.formId && data.type === GridSubjectType.CALLACTION && data[GridIdKeyConstant] === this.state[GridIdKeyConstant] && data[GridLinkIdKeyConstant] === this.state[GridLinkIdKeyConstant])
                )
            ).subscribe(
                (data: GridSubject) => {
                    if (this._ref) {
                        if (data.value.startsWith("Run") && data.containerElementId !== StringHelper.elementId(this.elementId)) {
                            return;
                        }
                        if (this.state[GridCloseActionKeyConstant].indexOf(data.value) > -1) {
                            if (data.containerElementId !== StringHelper.elementId(this.elementId)) {
                                return;
                            }
                            FormValidationService.resetRequired(this._ref);
                        }
                        GridHelper.detailContainer(this._ref, this.state, data, (this as any).___props);
                    }
                }
            );
        }
    }

    destroy(): void {
        this.destroyDone();
    }

    destroyDone(): void {
    }

    /* for React */
    props: Props;
    state: State;

    private _gridSubject: Subscription;
    private _ref: HTMLElement;

    constructor(props?: Props) {
        super(props);
        this.state = {};
        this._setRef = this._setRef.bind(this);
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        Debug.console.error(error, errorInfo);
    }

    render(): React.ReactNode {
        if (!this.displayMode || !this.state[GridIdKeyConstant]) {
            return null;
        } else {
            const attr = {
                [DomAttribute.GRIDID]: this.state[GridIdKeyConstant],
                [DomAttribute.GRIDLINKID]: this.state[GridLinkIdKeyConstant],
                [DomAttribute.GRIDCOMPONENT]: "_30_WaWcarRcbTZmSlJuZ4s14uuTOd1a"
            };
            const className = "m-0 p-0 w-100" + (this.state.containerClassName ? " " + this.state.containerClassName : "");
            return (
                <React.Fragment>
                    {GridHelper.infoGridContainer(this.parentDom, this.state[GridIdKeyConstant], "Detail Container - Header")}
                    <div ref={this._setRef} className={className} style={this.state.containerStyle} {...attr}>
                        {this._container("header")}
                        {GridHelper.indicatorGridContainer("Body")}
                        {this._container("body")}
                        {GridHelper.indicatorGridContainer("Footer")}
                        {this._container("footer")}
                    </div>
                </React.Fragment>
            );
        }
    }

    private _container(key: string) {
        const attr = {
            [DomAttribute.CONTAINER]: key,
            [DomAttribute.GRIDPK]: ""
        };
        if (key === "body") {
            attr[DomAttribute.GRIDBODYCONTAINER] = StringHelper.elementId(this.elementId);
        } else {
            attr[DomAttribute.GRIDACTIONCONTAINER] = StringHelper.elementId(this.elementId);
        }
        const className = "m-0 p-0 w-100" + (this.state[key + "ClassName"] ? " " + this.state[key + "ClassName"] : "");
        return (
            <div className={className} style={this.state[key + "Style"]}
                { ...attr }
            ></div>
        );
    }

    private _setRef(ref) {
        if (ref) {
            this._ref = ref;
            this.readyComponent();
        }
    }
}
